/**
 * StoreyNames — pure storey/level name normalization and matching logic.
 *
 * Phase 2 slice 3 (docs/architecture/PHASE2-SPATIAL-PLAN.md): bodies moved
 * VERBATIM from AutodeskViewer.tsx useCallbacks; the component keeps thin
 * useCallback wrappers under the original names (identical dep arrays) so all
 * consumer sites stay untouched. The state-dependent rvtLevelNameByIndex
 * aliasing stays in the component.
 *
 * 2026-07-06 update: WorkingE57PanoramaViewer's `storeyNamesMatch` and inline
 * Z-classifier had drifted into byte-equivalence with `levelNamesMatch`/
 * `getStoreyForZ`, so the old NO-GO no longer applied — that viewer now imports
 * both from here. Its `getPOIStoreyName` STOREY-EXTRACTION step remains
 * genuinely different from AutodeskViewer's hierarchy classifier (see
 * UNIFICATION-AUDIT-2026-07.md, P1 #8) — that divergence is a behavior
 * decision, not resolved by this module.
 */

export const normalizeLevelName = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).replace(/\s+/g, ' ').trim();
  return normalized || null;
};

export const normalizeRvtStoreyName = (value: unknown): string | null => {
  const normalized = normalizeLevelName(value);
  if (!normalized) return null;
  const stripped = normalized
    .replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/\s*\[\d+\]\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped || normalized;
};

export const levelNamesMatch = (left: string | null | undefined, right: string | null | undefined): boolean => {
  const normalizedLeft = normalizeLevelName(left);
  const normalizedRight = normalizeLevelName(right);
  if (!normalizedLeft || !normalizedRight) return false;

  const leftLower = normalizedLeft.toLowerCase();
  const rightLower = normalizedRight.toLowerCase();
  if (leftLower === rightLower) return true;

  // Word-boundary-aware match: "Level 1 - Floor Plan" matches "Level 1",
  // but "Level 10" does NOT match "Level 1" (needle followed by more digits).
  const boundaryMatch = (haystack: string, needle: string): boolean => {
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|\\b)${escaped}(?=$|\\b)(?!\\d)`, 'i').test(haystack);
  };
  return boundaryMatch(leftLower, rightLower) || boundaryMatch(rightLower, leftLower);
};

// Given a Z coordinate (in viewer/model space), find which storey it belongs to
// using range-based matching: Z between storey N and storey N+1 = storey N.
export const getStoreyForZ = (z: number, storeys: Array<{name: string, elevation: number}>): string | null => {
  if (!storeys || storeys.length === 0) return null;
  // Storeys are sorted by elevation ascending from fetchStoreys
  if (z < storeys[0].elevation) return storeys[0].name;
  for (let i = 0; i < storeys.length; i++) {
    const nextElev = i + 1 < storeys.length ? storeys[i + 1].elevation : Infinity;
    if (z < nextElev) return storeys[i].name;
  }
  return null;
};

/**
 * THE floor of the plan's floor list an asset is on (its index), or null when that cannot be told —
 * one rule for the 2D plan's asset icons and for Search on the plan (2026-10-02).
 * 1. Its level NAME, matched as everywhere else (levelNamesMatch).
 * 2. A name the plan does not know: WHERE THAT LEVEL IS — the models' own level list (`levels`,
 *    render-frame elevations) — and the plan floor at that height. Winery: a one-level model's assets
 *    say "Level ±0.000=63.333", the plan says "Level 0"; by name nothing matched and the Level 0 plan
 *    showed all 314 assets of every floor; by the assets' own heights its wall and ceiling items
 *    (2–3 m up) landed on "Level 1", which starts 1.25 m above Level 0. The level's height puts all
 *    81 on Level 0.
 * 3. No level: its own point (it sits at its level), then its shape's bottom.
 * `slack` (render units, about half a metre) puts a point on a floor's base on that floor; below the
 * lowest floor (pits, foundations) is the lowest floor, as getStoreyForZ has it.
 */
export const planFloorIndexForAsset = ({ storeyName, levels, pointZ, shapeMinZ, floors, slack = 0 }: {
  storeyName?: string | null;
  levels?: Array<{ name: string; elevation: number }> | null;
  pointZ?: number | null;
  shapeMinZ?: number | null;
  floors: Array<{ name: string | null; zMin: number | null; zMax: number | null }>;
  slack?: number;
}): number | null => {
  let lowest = -1;
  floors.forEach((floor, index) => {
    if (floor.zMin !== null && floor.zMax !== null && (lowest < 0 || floor.zMin < (floors[lowest].zMin as number))) lowest = index;
  });
  const floorAt = (z: number | null | undefined) => {
    if (typeof z !== 'number' || !Number.isFinite(z)) return -1;
    const at = z + slack;
    const inside = floors.findIndex((floor) => floor.zMin !== null && floor.zMax !== null && at >= floor.zMin && at < floor.zMax);
    if (inside >= 0) return inside;
    return lowest >= 0 && at < (floors[lowest].zMin as number) ? lowest : -1;
  };
  if (storeyName && storeyName !== 'Unassigned') {
    const byName = floors.findIndex((floor) => levelNamesMatch(floor.name, storeyName));
    if (byName >= 0) return byName;
    const level = levels?.find((candidate) => levelNamesMatch(candidate.name, storeyName));
    const byLevel = floorAt(level?.elevation);
    if (byLevel >= 0) return byLevel;
  }
  const byPoint = floorAt(pointZ);
  if (byPoint >= 0) return byPoint;
  const byShape = floorAt(shapeMinZ);
  return byShape >= 0 ? byShape : null;
};
